import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import {
  Contact,
  ContactOwner,
  UpdateContactPayload,
  EditFormValues,
} from 'types/crm/contacts';
import { tagChipStyle } from 'types/crm/tag';
import { useCrmValueOptions, useUpdateContactMutation } from 'service/crm';
import UserAvatar from 'components/common/UserAvatar';
import toast from 'react-hot-toast';
import TagSelect from 'components/common/TagSelect';
import ActionsDropdown from 'components/common/ActionsDropdown';
import FormInput from 'components/common/FormInput';
import { CustomSelect, SelectField } from 'components/common/CustomSelect';
import PhoneInput from 'components/common/PhoneInput';
import CountryList from 'country-list-with-dial-code-and-flag';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Form, Formik, FormikProps } from 'formik';
import { useDeleteContactMutation } from 'service/crm/hooks/useUpdateContactMutation';
import { useCrmNavigation } from 'hooks/useCrmNavigation';
import CopyableText from 'components/common/CopyableText';
import InlineOwnerField from '../Dropdowns/InlineOwnerFiled';
import { useRecordAccess } from 'context/crm/RecordAccessContext';
import DeleteModal, { CascadeItem } from 'components/Modals/CrmDeleteModal';
import FormTextarea from 'components/common/FormTextArea';
import { contactEditValidationSchema } from 'utils/validation';

interface ContactDetailsCardProps {
  contact: Contact;
  owners: ContactOwner[];
}

const buildInitialForm = (contact: Contact): EditFormValues => ({
  firstName: contact.firstName ?? '',
  lastName: contact.lastName ?? '',
  email: contact.email ?? '',
  phone: contact.phone ?? '',
  jobTitle: contact.jobTitle ?? '',
  linkedInUrl: contact.linkedInUrl ?? '',
  // The site belongs to the company, not the contact, so it is read off the
  // one they are linked to — the same place Create Contact fills it from.
  companyWebsiteUrl: contact.companies?.[0]?.websiteUrl ?? '',
  // The record already carries the CRM value's key, which is what the select
  // and the PATCH both use. No key/name translation to get wrong — and nothing
  // that silently blanks when an admin adds a value the app was not built with.
  lifecycleStage: contact.lifecycleStage ?? '',
  engagementStatus: contact.engagementStatus ?? '',
  contactOwnerUuid: contact.owner?.uuid ?? '',
  tags: contact.tags.map((t) => t.name),
  notes: contact.notes ?? '',
});

/** Static stand-in for an inline-editable select when the user is view-only. */
const ReadOnlyField: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="rounded-lg border border-primary-subtle bg-primary-surface px-3 py-2">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-medium">
      {label}
    </div>
    <div className="mt-0.5 text-sm text-neutral">{value || '—'}</div>
  </div>
);

const ContactDetailsCard: React.FC<ContactDetailsCardProps> = ({ contact, owners }) => {
  // Resolved once by <RecordAccessProvider> on the detail page.
  const { canEdit, canDelete, canReassignOwner } = useRecordAccess();

  const updateContact = useUpdateContactMutation();
  const [isEditing, setIsEditing] = useState(false);

  // Lifecycle / Engagement are changed deliberately — Edit, pick, Save — not
  // the moment a dropdown is touched. A stray click on a select should not
  // move a contact to a new stage.
  const [isEditingStages, setIsEditingStages] = useState(false);
  const [stageDraft, setStageDraft] = useState({
    lifecycleStage: '',
    engagementStatus: '',
  });
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);

  // The organisation's configured values, not a list compiled into the app.
  // The contact's own value is passed in so it stays selectable even once an
  // admin has deactivated it.
  const { options: lifecycleStageOptions } = useCrmValueOptions(
    'LIFECYCLE_STAGE',
    contact.lifecycleStage,
    contact.lifecycleStageLabel,
  );
  const { options: engagementStatusOptions } = useCrmValueOptions(
    'ENGAGEMENT_STATUS',
    contact.engagementStatus,
    contact.engagementStatusLabel,
  );

  const deleteContact = useDeleteContactMutation();
  const { toContacts } = useCrmNavigation();

  /**
   * Section 23: when the grant disappears (Task/Note reassigned, Owner changed)
   * the user drops back to view-only. The refetched contact flips `canEdit`, so
   * collapse any open edit surface instead of leaving a dead form on screen.
   */
  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
      setDeleteTarget(null);
    }
  }, [canEdit]);

  // --- Phone (country code + national number split, same approach as CreateContact) ---
  const countries = useMemo(() => CountryList.getAll(), []);
  const [phoneCode, setPhoneCode] = useState('+91');
  const [phoneNumberVal, setPhoneNumberVal] = useState('');

  const syncPhoneFromValue = (rawNum: string | null | undefined) => {
    const value = rawNum || '';
    if (value) {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed) {
        setPhoneCode(`+${parsed.countryCallingCode}`);
        setPhoneNumberVal(parsed.nationalNumber);
      } else {
        const matchedCountry = countries.find((c: any) => value.startsWith(c.dial_code));
        if (matchedCountry) {
          setPhoneCode(matchedCountry.dial_code);
          setPhoneNumberVal(value.slice(matchedCountry.dial_code.length));
        } else {
          setPhoneNumberVal(value);
        }
      }
    } else {
      setPhoneCode('+91');
      setPhoneNumberVal('');
    }
  };

  useEffect(() => {
    syncPhoneFromValue(contact.phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.phone, countries]);

  const ownerOptions = useMemo(
    () => owners.map((o: ContactOwner) => ({ value: o.uuid, label: o.name.trim() })),
    [owners],
  );

  const handleEdit = () => {
    if (!canEdit) return;
    syncPhoneFromValue(contact.phone);
    // The full form edits these too; a half-finished stage edit should not be
    // waiting underneath it when the form closes.
    setIsEditingStages(false);
    setIsEditing(true);
  };

  /**
   * PATCH a single field. Returns the promise so the stage editor /
   * InlineOwnerField can roll back their optimistic value if the request fails.
   */
  const patchContact = (patch: Partial<EditFormValues>) => {
    if (!canEdit) {
      const err = new Error('You do not have permission to update this contact.');
      toast.error(err.message);
      return Promise.reject(err);
    }
    return updateContact.mutateAsync({
      contactUuid: contact.uuid,
      ...patch,
    } as UpdateContactPayload);
  };

  const startStageEdit = () => {
    setStageDraft({
      lifecycleStage: contact.lifecycleStage ?? '',
      engagementStatus: contact.engagementStatus ?? '',
    });
    setIsEditingStages(true);
  };

  const saveStages = () => {
    // Only what actually changed is sent.
    const patch: Partial<EditFormValues> = {};
    if (stageDraft.lifecycleStage !== (contact.lifecycleStage ?? '')) {
      patch.lifecycleStage = stageDraft.lifecycleStage;
    }
    if (stageDraft.engagementStatus !== (contact.engagementStatus ?? '')) {
      patch.engagementStatus = stageDraft.engagementStatus;
    }

    if (Object.keys(patch).length === 0) {
      setIsEditingStages(false);
      return;
    }

    patchContact(patch)
      .then(() => {
        toast.success('Contact updated');
        setIsEditingStages(false);
      })
      // Stays open on failure, so the choice is not lost along with the save.
      .catch((err: any) =>
        toast.error(err?.message ?? 'Failed to update contact. Please try again.'),
      );
  };

  const handlePhoneCodeChange = (
    newCode: string,
    setFieldValue: FormikProps<EditFormValues>['setFieldValue'],
  ) => {
    setPhoneCode(newCode);
    setFieldValue('phone', phoneNumberVal ? `${newCode}${phoneNumberVal}` : '');
  };

  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikProps<EditFormValues>['setFieldValue'],
  ) => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 11);
    setPhoneNumberVal(cleaned);
    setFieldValue('phone', cleaned ? `${phoneCode}${cleaned}` : '');
  };

  const handleSaveChanges = (values: EditFormValues) => {
    if (!canEdit) {
      toast.error('You do not have permission to update this contact.');
      setIsEditing(false);
      return;
    }

    // Ownership is Admin/Manager only — never let a CRM User's form submit carry
    // an owner change, even if state was seeded with one.
    const { contactOwnerUuid, ...rest } = values;
    const payload = {
      contactUuid: contact.uuid,
      ...rest,
      ...(canReassignOwner ? { contactOwnerUuid } : {}),
    } as UpdateContactPayload;

    updateContact.mutate(payload, {
      onError: (err: any) => {
        toast.error(err?.message ?? 'Failed to update contact. Please try again.');
      },
      onSuccess: () => {
        toast.success('Contact updated');
        setIsEditing(false);
      },
    });
  };

  const handleDelete = () => {
    if (!canDelete) return;
      // console.log('delete target', contact);
    setDeleteTarget(contact);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget || !canDelete) return;
    deleteContact.mutate(deleteTarget.uuid, {
      onSuccess: () => {
        toast.success('Contact deleted');
        setDeleteTarget(null);
        toContacts();
      },
      onError: (err: any) => {
        toast.error(err?.message ?? 'Failed to delete contact. Please try again.');
      },
    });
  };

  const handleCancelDelete = () => setDeleteTarget(null);

const deleteCascade = useMemo<CascadeItem[]>(() => {
  if (!deleteTarget) return [];

  const { fullName, notesCount, tasksCount, activitiesCount } = deleteTarget;

  return [
    { count: 1, label: `Contact — ${fullName}` },
    ...(notesCount ? [{ count: notesCount, label: 'Note' }] : []),
    ...(tasksCount ? [{ count: tasksCount, label: 'Task' }] : []),
    ...(activitiesCount
      ? [{ count: activitiesCount, label: 'Logged activity', labelPlural: 'Logged activities' }]
      : []),
  ];
}, [deleteTarget]);
  const rowActions = useMemo(() => {
    const actions: Array<{
      label: string;
      icon: React.ReactNode;
      variant?: 'danger';
      onClick: () => void;
    }> = [];

    if (canEdit) {
      actions.push({
        label: 'Edit Contact',
        icon: <Pencil className="h-4 w-4" />,
        onClick: handleEdit,
      });
    }
    if (canDelete) {
      actions.push({
        label: 'Delete Contact',
        icon: <Trash2 className="h-4 w-4" />,
        variant: 'danger',
        onClick: handleDelete,
      });
    }
    return actions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, canDelete, contact.uuid]);

  if (isEditing && canEdit) {
    return (
      <Formik<EditFormValues>
        key={contact.uuid}
        initialValues={buildInitialForm(contact)}
        enableReinitialize
        validationSchema={contactEditValidationSchema}
        onSubmit={handleSaveChanges}
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, handleBlur }) => (
          <Form
            onSubmit={handleSubmit}
            className="w-full h-full rounded-l-xl bg-primary border-r border-primary-subtle p-4 space-y-5 overflow-auto"
          >
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <UserAvatar
                  name={contact.fullName}
                  profileImageURL={contact.profileImageUrl ?? undefined}
                  className="w-12 h-12 text-base"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={updateContact.isPending}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-blue-500 disabled:opacity-60"
                >
                  {updateContact.isPending ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  disabled={updateContact.isPending}
                  className="p-1.5 rounded-lg text-neutral-medium hover:text-neutral hover:bg-primary-subtle/50"
                  aria-label="Cancel edit"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Name fields */}
            <div className="flex gap-3">
              <FormInput
                label="First Name"
                name="firstName"
                value={values.firstName}
                placeholder="First name"
                onChange={handleChange}
                darkBg
                className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
              />
              <FormInput
                label="Last Name"
                name="lastName"
                value={values.lastName}
                placeholder="Last name"
                onChange={handleChange}
                darkBg
                className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
              />
            </div>

            {/* Lifecycle / Engagement */}
            <div className="space-y-3 rounded-lg bg-primary-subtle/30">
              <CustomSelect
                label="Lifecycle stage"
                name="lifecycleStage"
                options={lifecycleStageOptions}
                placeholder="Select Lifecycle Stage"
                darkBg
                className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
              />
              <CustomSelect
                label="Engagement status"
                name="engagementStatus"
                options={engagementStatusOptions}
                placeholder="Select Engagement Status"
                darkBg
                className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
              />
            </div>

            {/* Contact info */}
            <div className="overflow-hidden">
              <div className="text-[11px] tracking-wider font-semibold uppercase text-neutral pb-4">
                Contact Info
              </div>
              <div className="space-y-3">
                <FormInput
                  label="Email"
                  name="email"
                  value={values.email}
                  placeholder="name@company.com"
                  onChange={handleChange}
                  darkBg
                  className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
                />
                <PhoneInput
                  label="Phone"
                  name="phone"
                  value={phoneNumberVal}
                  code={phoneCode}
                  data={countries}
                  setCode={(code) => handlePhoneCodeChange(code, setFieldValue)}
                  onChange={(e) => handlePhoneChange(e, setFieldValue)}
                  placeholder="Enter phone number"
                  darkBg={true}
                  className={{
                    input: 'h-[40px]',
                    field: 'text-sm px-3 placeholder:text-sm',
                    label: 'text-xs text-neutral-medium',
                  }}
                />
                <FormInput
                  label="Job Title"
                  name="jobTitle"
                  value={values.jobTitle}
                  placeholder="Job title"
                  onChange={handleChange}
                  darkBg
                  className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
                />
                <FormInput
                  label="LinkedIn"
                  name="linkedInUrl"
                  value={values.linkedInUrl}
                  placeholder="linkedin.com/in/..."
                  onChange={handleChange}
                  onBlur={handleBlur}
                  darkBg
                  error={touched.linkedInUrl && errors.linkedInUrl}
                  className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
                />
                <FormInput
                  label="Company Website"
                  name="companyWebsiteUrl"
                  value={values.companyWebsiteUrl}
                  placeholder="company.com"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  darkBg
                  error={touched.companyWebsiteUrl && errors.companyWebsiteUrl}
                  className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="text-xs text-[#9ca3af] font-medium block mb-2">Tags</label>
              <TagSelect
                value={values.tags}
                onChange={(tags) => setFieldValue('tags', tags)}
                recordType="CONTACT"
                currentTags={contact.tags}
              />
            </div>

            {/* Owner — Admin/Manager may reassign; everyone else sees it read-only */}
            {canReassignOwner ? (
              <CustomSelect
                label="Owner"
                name="contactOwnerUuid"
                options={ownerOptions}
                onBlur={handleBlur}
                placeholder="Select owner"
                darkBg
                className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
              />
            ) : (
              <ReadOnlyField label="Owner" value={contact.owner?.name} />
            )}

            {/* Notes */}
            <FormTextarea
              name="notes"
              label="Insights"
              placeholder="Add insights about this contact"
              maxLength={500}
              // darkBg
              className={{ label: 'text-sm', textarea: 'text-sm' }}
            />
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <div className="w-full h-full rounded-l-xl bg-primary border-r border-primary-subtle p-4 space-y-5 overflow-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <UserAvatar
            name={contact.fullName}
            profileImageURL={contact.profileImageUrl ?? undefined}
            className="w-12 h-12 text-base"
          />
          <div>
            <h3 className="text-base font-semibold text-neutral capitalize">{contact.fullName}</h3>
            <p className="text-xs text-neutral-faded">{contact.jobTitle}</p>
          </div>
        </div>
        {rowActions.length > 0 && <ActionsDropdown actions={rowActions} />}
      </div>

      {/* Lifecycle / Engagement — read-only until Edit is pressed */}
      <div className="space-y-3">
        {isEditingStages && canEdit ? (
          <>
            <SelectField
              label="Lifecycle Stage"
              name="lifecycleStage"
              value={stageDraft.lifecycleStage}
              onChange={(next) =>
                setStageDraft((draft) => ({ ...draft, lifecycleStage: next }))
              }
              options={lifecycleStageOptions}
              placeholder="Select lifecycle stage"
              disabled={updateContact.isPending}
              darkBg
              className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
            />
            <SelectField
              label="Engagement Status"
              name="engagementStatus"
              value={stageDraft.engagementStatus}
              onChange={(next) =>
                setStageDraft((draft) => ({ ...draft, engagementStatus: next }))
              }
              options={engagementStatusOptions}
              placeholder="Select engagement status"
              disabled={updateContact.isPending}
              darkBg
              className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsEditingStages(false)}
                disabled={updateContact.isPending}
                className="rounded-lg border border-primary-subtle px-3 py-1.5 text-xs font-medium text-neutral-medium transition-colors hover:bg-white/5 hover:text-white disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveStages}
                disabled={updateContact.isPending}
                className="button-active rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                {updateContact.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          <>
            {canEdit && (
              <div className="-mb-1 flex justify-end">
                <button
                  type="button"
                  onClick={startStageEdit}
                  className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-neutral-medium transition-colors hover:bg-white/5 hover:text-white"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
              </div>
            )}
            {/* The record's own label, which is already the configured name —
                no need to wait on the options list to read it back. */}
            <ReadOnlyField
              label="Lifecycle Stage"
              value={contact.lifecycleStageLabel}
            />
            <ReadOnlyField
              label="Engagement Status"
              value={contact.engagementStatusLabel}
            />
          </>
        )}
      </div>

      {/* Contact info — always visible (section 4) */}
      <div className="overflow-hidden">
        <div className="text-[11px] tracking-wider font-semibold uppercase text-neutral-medium pb-2">
          Contact Info
        </div>
        <div className="rounded-lg border border-primary-subtle bg-primary-surface pb-1">
          {[
            { label: 'Email', value: contact.email, isLink: true, copyable: true },
            {
              label: 'Phone',
              value: contact.phoneNumber
                ? `${contact.countryCode ? (contact.countryCode.startsWith('+') ? contact.countryCode : `+${contact.countryCode}`) + ' ' : ''}${contact.phoneNumber}`
                : null,
              copyable: true,
            },
            { label: 'LinkedIn', value: contact.linkedInUrl, copyable: true },
            // Editable from this card, so it has to be readable from it too.
            {
              label: 'Company Website',
              value: contact.companies?.[0]?.websiteUrl,
              copyable: true,
            },
          ].map((row) => (
            <div
              key={row.label}
              className="px-3 py-2 border-t border-primary-subtle first:border-t-0 "
            >
              <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-medium">
                {row.label}
              </div>

              {row.copyable ? (
                <CopyableText
                  value={row.value ?? undefined}
                  className="mt-0.5"
                  textClassName={`text-sm ${row.isLink ? 'text-blue-400' : 'text-neutral'}`}
                  truncate
                />
              ) : (
                <div className="text-sm mt-0.5 text-neutral capitalize">{row.value || '—'}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div>
        <div className="text-[11px] tracking-wider font-semibold uppercase text-neutral-medium pb-2">
          Tags
        </div>
        {contact.tags.length > 0 ? (
          <div className="flex flex-wrap border border-primary-subtle py-3 px-3 rounded-lg bg-primary-surface gap-1.5">
            {contact.tags.map((tag) => (
              <span
                key={tag.name}
                style={tagChipStyle(tag.colorKey)}
                className="rounded-full border px-2.5 py-1 text-xs font-medium"
              >
                {tag.name}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-sm text-neutral-medium">—</div>
        )}
      </div>

      {/* Owner — inline reassign for Admin/Manager, static otherwise */}
      <InlineOwnerField
        label="Owner"
        owners={owners}
        value={contact.owner?.uuid ?? null}
        editable={canReassignOwner}
        onSave={(ownerUuid: any) => patchContact({ contactOwnerUuid: ownerUuid ?? '' })}
        errorMessage="Could not reassign this contact. Please try again."
      />

      {/* Notes */}
      <div>
        <div className="text-[11px] font-medium tracking-wide uppercase text-neutral-medium pt-2 pb-2">
          Insights
        </div>
        <div className="py-2 px-3 rounded-lg bg-primary-surface border border-primary-subtle max-h-32 overflow-y-auto">
          <p className="text-sm text-neutral-faded whitespace-pre-wrap [overflow-wrap:anywhere]">
            {contact.notes || '—'}
          </p>
        </div>
      </div>
      <DeleteModal
        isOpen={!!deleteTarget}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteContact.isPending}
        entityLabel="Contact"
        entityName={deleteTarget?.fullName}
        description={
          deleteTarget ? (
            <>
              <strong className="text-white font-semibold">{deleteTarget.fullName}</strong> will be
              permanently deleted from your CRM.
            </>
          ) : undefined
        }
        cascade={deleteCascade}
        preserved="All associated Deals and Companies will remain in your CRM — only their association with this contact is removed."
      />
    </div>
  );
};

export default ContactDetailsCard;